import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import type {
    IR2Attachment,
    IR2FileDownloadCheckResponse,
    IR2FileDownloadStartResponse,
    IR2FileLimitsResponse,
    IR2FileUploadCheckResponse,
    IR2FileUploadStartResponse,
} from '@common/types/api/r2-types';
import
{
    R2AttachmentType,
    R2FileCheckResult,
    R2FileDownloadStartResult,
    R2FileUploadStartResult,
} from '@common/types/api/r2-types';
import { Observable, from } from 'rxjs';
import { SessionCryptoService } from '@services/session-crypto/session-crypto.service';

interface IStoredFileUrl
{
    url: string;
    expiresAt: number;
}
@Injectable({ providedIn: 'root' })
export class R2FileService
{
    private readonly _sessionCrypto = inject(SessionCryptoService);
    private readonly _inflight = new Map<string, Promise<string>>();

    // readonly fileUrls = signal(new Map<string, string>());
    readonly fileUrlErrors = signal(new Set<string>());
    readonly fileUrls = signal(new Map<string, IStoredFileUrl>());

    private readonly POLL_INTERVAL_MS = 1_000;
    private readonly POLL_MAX_ATTEMPTS = 30;
    private readonly FILE_URL_TTL_MS = 50 * 60 * 1_000;
    private readonly STORAGE_PREFIX = 'file-url:';

    private _limits: IR2FileLimitsResponse | null = null;
    private _limitsPromise: Promise<IR2FileLimitsResponse> | null = null;

    getLimits(): Promise<IR2FileLimitsResponse>
    {
        if (this._limits) return Promise.resolve(this._limits);
        if (this._limitsPromise) return this._limitsPromise;

        this._limitsPromise = this._sessionCrypto.getAddin<IR2FileLimitsResponse>('/file/limits/get')
            .then((res) =>
            {
                this._limits = res;
                this._limitsPromise = null;
                return res;
            })
            .catch((err) =>
            {
                this._limitsPromise = null;
                throw err;
            });

        return this._limitsPromise;
    }

    /** Cached SAS URL for a file id, or undefined when it is absent or already expired. */
    peekFileUrl(fileId: string): string | undefined
    {
        const entry = this.fileUrls().get(fileId);
        return entry && Date.now() < entry.expiresAt ? entry.url : undefined;
    }

    /** Fire-and-forget refresh used by templates; re-resolves when the cached URL is missing or expired. */
    loadFileUrl(fileId: string, copilotId: string): void
    {
        if (this.peekFileUrl(fileId) || this.fileUrlErrors().has(fileId)) return;
        void this.resolveFileUrl(fileId, copilotId).catch(() => undefined);
    }

    clearFileUrlError(fileId: string): void
    {
        this.fileUrlErrors.update((set) =>
        {
            if (!set.has(fileId)) return set;

            const next = new Set(set);
            next.delete(fileId);
            return next;
        });
    }

    /**
     * Resolve a non-expired downloadable SAS URL, fetching a fresh one when the
     * cached entry is missing or expired. Concurrent callers for the same file id
     * are coalesced onto a single in-flight request.
     */
    async resolveFileUrl(fileId: string, copilotId: string): Promise<string>
    {
        const cached = this.peekFileUrl(fileId);
        if (cached) return cached;

        const existing = this._inflight.get(fileId);
        if (existing) return existing;

        const promise = this._resolveFileUrl(fileId, copilotId)
            .then((entry) =>
            {
                this.fileUrls.update((map) => new Map(map).set(fileId, entry));
                this.clearFileUrlError(fileId);
                return entry.url;
            })
            .catch((error: unknown) =>
            {
                console.error(`Failed to resolve file URL for fileId=${ fileId }:`, error);
                if (this.isServiceMaintenanceError(error))
                {
                    this.fileUrlErrors.update((set) => new Set(set).add(fileId));
                }

                throw error;
            })
            .finally(() =>
            {
                this._inflight.delete(fileId);
            });

        this._inflight.set(fileId, promise);
        return promise;
    }

    private isServiceMaintenanceError(error: unknown): boolean
    {
        return (error as HttpErrorResponse)?.error?.details?.result === R2FileDownloadStartResult.FailServiceMaintenance;
    }

    private async _resolveFileUrl(fileId: string, copilotId: string): Promise<IStoredFileUrl>
    {
        const storageKey = `${ this.STORAGE_PREFIX }${ fileId }`;
        const stored = localStorage.getItem(storageKey);

        if (stored)
        {
            const entry: IStoredFileUrl = JSON.parse(stored);
            if (Date.now() < entry.expiresAt)
            {
                return entry;
            }
            localStorage.removeItem(storageKey);
        }

        const url = await this._fetchFileUrl(fileId, copilotId);
        const entry: IStoredFileUrl = { url, expiresAt: Date.now() + this.FILE_URL_TTL_MS };
        localStorage.setItem(storageKey, JSON.stringify(entry));
        return entry;
    }

    private async _fetchFileUrl(fileId: string, copilotId: string): Promise<string>
    {
        // Start and check are conservative writes. A lost response must not
        // restart the operation; only an explicit user action may try again.
        const startRes = await this._sessionCrypto.postAddin<unknown, IR2FileDownloadStartResponse>(
            '/file/download/start', {
            copilot_id: copilotId,
            file_id: fileId,
        },
        );
        if (startRes.result < R2FileDownloadStartResult.Success || !startRes.operation_id)
        {
            throw new Error(`Download start failed: result=${ startRes.result }`);
        }
        return this._pollDownloadCheck(startRes.operation_id);
    }

    private async _pollDownloadCheck(operationId: number): Promise<string>
    {
        for (let attempt = 0; attempt < this.POLL_MAX_ATTEMPTS; attempt++)
        {
            const res = await this._sessionCrypto.postAddin<unknown, IR2FileDownloadCheckResponse>(
                '/file/download/check', {
                operation_id: operationId,
            },
            );

            if (res.result === R2FileCheckResult.Success && res.sas_url)
            {
                return res.sas_url;
            }

            if (res.result < R2FileCheckResult.Success)
            {
                throw new Error(`Download check failed: result=${ res.result }`);
            }

            await this._delay(this.POLL_INTERVAL_MS);
        }

        throw new Error(`Download check timed out after ${ this.POLL_MAX_ATTEMPTS } attempts`);
    }

    /**
     * Uploads a file. `onStart` fires with the server operation id the moment
     * the upload is accepted (before the blob PUT), so callers can persist it
     * and later {@link resumeUpload} a poll that was interrupted by a reload.
     */
    uploadFile(
        file: File,
        copilotId: string,
        onStart?: (operationId: number) => void,
    ): Observable<IR2Attachment>
    {
        return from(this._doUpload(file, copilotId, onStart));
    }

    /**
     * Resumes polling for an upload whose operation id was persisted. Rebuilds
     * the attachment from the persisted file metadata since the original File
     * object is no longer available after a reload.
     */
    resumeUpload(
        operationId: number,
        meta: { name: string; mimeType: string; fileSize: number; },
    ): Promise<IR2Attachment>
    {
        return this._pollUploadCheck(operationId).then((fileId) => ({
            type: R2AttachmentType.File,
            mime_type: meta.mimeType,
            name: meta.name,
            file_id: fileId,
            file_size: meta.fileSize,
        }));
    }

    private async _doUpload(
        file: File,
        copilotId: string,
        onStart?: (operationId: number) => void,
    ): Promise<IR2Attachment>
    {
        const mimeType = file.type || 'application/octet-stream';

        const startRes = await this._sessionCrypto.postAddin<unknown, IR2FileUploadStartResponse>(
            '/file/upload/start', {
            copilot_id: copilotId,
            file_name: file.name,
            mime_type: mimeType,
            file_size: file.size,
        },
        );

        if (startRes.result < R2FileUploadStartResult.Success || !startRes.operation_id || !startRes.sas_url)
        {
            throw new Error(`Upload start failed: result=${ startRes.result }`);
        }

        onStart?.(startRes.operation_id);

        // Direct PUT to Azure blob storage; native fetch so HTTP interceptors
        // (api host rewrite, device-hash header) don't touch the SAS request.
        const putRes = await fetch(startRes.sas_url, {
            method: 'PUT',
            headers: {
                'Content-Type': mimeType,
                'x-ms-blob-type': 'BlockBlob',
            },
            body: file,
        });

        if (!putRes.ok)
        {
            throw new Error(`SAS upload failed: ${ putRes.status }`);
        }

        const fileId = await this._pollUploadCheck(startRes.operation_id);

        return {
            type: R2AttachmentType.File,
            mime_type: mimeType,
            name: file.name,
            file_id: fileId,
            file_size: file.size,
        };
    }

    private async _pollUploadCheck(operationId: number): Promise<string>
    {
        for (let attempt = 0; attempt < this.POLL_MAX_ATTEMPTS; attempt++)
        {
            const res = await this._sessionCrypto.postAddin<unknown, IR2FileUploadCheckResponse>(
                '/file/upload/check', {
                operation_id: operationId,
            },
            );

            if (res.result === R2FileCheckResult.Success && res.file_id)
            {
                return res.file_id;
            }

            if (res.result < R2FileCheckResult.Success && attempt === this.POLL_MAX_ATTEMPTS - 1)
            {
                throw new Error(`Upload check failed: result=${ res.result }`);
            }

            await this._delay(this.POLL_INTERVAL_MS);
        }

        throw new Error(`Upload check timed out after ${ this.POLL_MAX_ATTEMPTS } attempts`);
    }

    private _delay(ms: number): Promise<void>
    {
        return new Promise<void>((resolve) => setTimeout(resolve, ms));
    }
}
